import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Section from "../components/base/Section.jsx";
import Input from "../components/base/Input.jsx";
import Helper from "../components/base/Helper.jsx";
import ConnexionCard from "../components/ConnexionCard.jsx";
import Form from "../components/base/Form.jsx";
import Select from "../components/base/Select.jsx";
import PhoneInput from "../components/base/PhoneInput.jsx";

const API_URL = process.env.REACT_APP_API_URL;

export default function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Suppress Supabase session synchronization errors globally
  useEffect(() => {
    const handleError = (event) => {
      if (event.message && (
        event.message.includes("Session synchronization") ||
        event.message.includes("Lock was stolen") ||
        event.message.includes("multiple tabs")
      )) {
        event.preventDefault();
        console.warn("Suppressed Supabase session error:", event.message);
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", (event) => {
      if (event.reason && event.reason.message && (
        event.reason.message.includes("Session synchronization") ||
        event.reason.message.includes("Lock was stolen") ||
        event.reason.message.includes("multiple tabs")
      )) {
        event.preventDefault();
        console.warn("Suppressed Supabase session rejection:", event.reason.message);
      }
    });

    // Clear stale Supabase locks on component mount
    try {
      const keysToCheck = [
        'sb-mdidlwnnrgzwjwgydotv-auth-token',
        'sb-mdidlwnnrgzwjwgydotv-session',
        'supabase.session',
        'supabase.access_token'
      ];
      keysToCheck.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.debug(`Could not remove ${key}:`, e);
        }
      });
    } catch (e) {
      console.debug("Error clearing Supabase session on mount:", e);
    }

    return () => {
      window.removeEventListener("error", handleError);
    };
  }, []);

  const isFormValid = formData.firstname && formData.lastname && formData.dateOfBirth && formData.gender;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      setLoading(false);
      return;
    }

    console.log("Submitting signup form:", formData);

    // Clear any stale Supabase session locks before making requests
    try {
      const keysToCheck = [
        'sb-mdidlwnnrgzwjwgydotv-auth-token',
        'sb-mdidlwnnrgzwjwgydotv-session',
        'supabase.session',
        'supabase.access_token'
      ];
      keysToCheck.forEach(key => {
        if (localStorage.getItem(key)) {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            console.warn(`Could not remove ${key}:`, e);
          }
        }
      });
    } catch (err) {
      console.warn("Error clearing session locks:", err);
    }

    try {
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",  // Include cookies/session credentials
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de la création du compte");
      }

      const data = await response.json();
      localStorage.setItem("participant", JSON.stringify(data.participant));
      localStorage.setItem("user", JSON.stringify(data.user));
      console.log("Account created:", data);
      navigate("/user", { replace: true });
    } catch (err) {
      setError(err.message || "Erreur lors de la création du compte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConnexionCard brand="BIBbib" title="Créer un compte" subtitle="Rejoignez-nous pour réserver vos courses">

        <Section title={null}>
          <Form onSubmit={handleSubmit} disabled={!isFormValid} >
            <div style={styles.row}>
              <Input label="Prénom" name="firstname" value={formData.firstname} setFormData={setFormData} type="text" />
              <Input label="Nom" name="lastname" value={formData.lastname} setFormData={setFormData} type="text" />
            </div>
            <Input label="Email" name="email" value={formData.email} setFormData={setFormData} type="email" />
            <PhoneInput label="Téléphone (optionnel)" name="phone" phoneValuee={formData.phone} setFormData={setFormData} type="tel" />
            <div style={styles.row}>
                <Input 
                  label="Date de naissance" 
                  name="dateOfBirth" 
                  type="date" 
                  value={formData.dateOfBirth} 
                  setFormData={setFormData} 
                />
                <Select 
                    name="gender" 
                    label="Genre" 
                    dict={{ M: "Homme", F: "Femme", O: "Autre" }} 
                    value={formData.gender} 
                    setFormData={setFormData} 
                />
            </div>
            <Input label="Mot de passe" name="password" value={formData.password} setFormData={setFormData} type="password" />
            <Input label="Confirmer le mot de passe" name="confirmPassword" value={formData.confirmPassword} setFormData={setFormData} type="password" />
            
            {error && <p style={styles.error}>{error}</p>}
          </Form>
        </Section>

        <Helper title="Vous avez déjà un compte ?" cta="Se connecter" to="/login" />
      </ConnexionCard>
  );
}

const styles = {
  
  form: {
    display: "grid",
    gap: 12,
    alignItems: "center",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    verticalAlign: "middle",
  },
  error: {
    color: "#b91c1c",
    fontSize: 13,
    margin: 0,
    textAlign: "center",
  },
};
